import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { SHIPMENT, shipmentMeta } from "@/lib/shipment";
import { Loader2, CheckCircle2, Clock, PhoneCall, IdCard, MapPin, Mail, Truck, Route, Calendar } from "lucide-react";

const STEPS = [
  { key: "new", label: "Application received", icon: CheckCircle2 },
  { key: "contacted", label: "Recruiter contacted you", icon: PhoneCall },
  { key: "hired", label: "Hired — welcome aboard!", icon: CheckCircle2 },
];
const ORDER = { new: 0, contacted: 1, hired: 2 };

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [app, setApp] = useState(undefined); // undefined loading, null none, object
  const [loads, setLoads] = useState([]);
  useEffect(() => {
    api.get("/my/application").then((r) => setApp(r.data)).catch(() => setApp(null));
    api.get("/my/assignments").then((r) => setLoads(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (id, shipment_status) => {
    try {
      const { data } = await api.patch(`/my/assignments/${id}/status`, { shipment_status });
      setLoads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const current = app ? ORDER[app.status] ?? 0 : 0;

  return (
    <DashboardShell title={`Hi, ${user?.name || "driver"}`} subtitle="Your partner application with KS&Y Transport.">
      {app === undefined ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-ksy-blue animate-spin" /></div>
      ) : app === null ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="no-application">
          No application on file yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6" data-testid="partner-dashboard">
          {/* status stepper */}
          <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-8">
            <h2 className="font-display font-bold text-xl text-ksy-navy mb-6">Application status</h2>
            <div className="space-y-6">
              {STEPS.map((s, i) => {
                const done = i <= current;
                const Icon = i < current ? CheckCircle2 : done ? s.icon : Clock;
                return (
                  <div key={s.key} className="flex items-start gap-4" data-testid={`step-${s.key}`}>
                    <span className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${done ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="pt-1.5">
                      <div className={`font-semibold ${done ? "text-ksy-navy" : "text-slate-400"}`}>{s.label}</div>
                      {i === current && <div className="text-sm text-ksy-blue font-semibold mt-0.5">Current stage</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* details */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3 text-sm h-fit" data-testid="partner-details">
            <h3 className="font-display font-bold text-ksy-navy mb-2">Your details</h3>
            <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4" />{app.email}</div>
            <div className="flex items-center gap-2 text-slate-600"><IdCard className="w-4 h-4 text-ksy-blue" />Experience: {app.cdl_experience}</div>
            <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4" />{app.city || "—"}, {app.state || "—"}</div>
            {app.endorsements && <div className="text-slate-600">Endorsements: {app.endorsements}</div>}
            {app.license_path ? (
              <div className="flex items-center gap-2 text-green-600 font-semibold pt-1"><CheckCircle2 className="w-4 h-4" /> License on file</div>
            ) : (
              <div className="text-slate-400 pt-1">No license uploaded</div>
            )}
          </div>

          {/* Assigned loads */}
          <div className="md:col-span-3">
            <h2 className="font-display font-bold text-xl text-ksy-navy mb-4">Assigned loads</h2>
            {loads.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="no-loads">
                No loads assigned yet. Your dispatcher will assign routes here.
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="loads-list">
                {loads.map((l) => (
                  <div key={l.id} className="p-5" data-testid={`load-${l.id}`}>
                    <div className="flex flex-wrap items-center gap-4 justify-between">
                      <div>
                        <div className="flex items-center gap-2 font-semibold text-ksy-navy"><MapPin className="w-4 h-4 text-ksy-blue" />{l.origin} → {l.destination}</div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1"><Route className="w-3.5 h-3.5" />{Number(l.miles).toLocaleString()} mi</span>
                          <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" />{l.num_cars} vehicle(s) · {l.transport_type}</span>
                          {l.pickup_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{l.pickup_date}</span>}
                        </div>
                      </div>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${shipmentMeta(l.shipment_status).color}`}>
                        {shipmentMeta(l.shipment_status).label}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {SHIPMENT.map((s) => (
                        <button
                          key={s.key}
                          onClick={() => updateStatus(l.id, s.key)}
                          disabled={l.shipment_status === s.key}
                          className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${l.shipment_status === s.key ? "bg-ksy-navy text-white border-ksy-navy" : "bg-white text-slate-600 border-slate-200 hover:border-ksy-blue hover:text-ksy-blue"}`}
                          data-testid={`load-status-${s.key}-${l.id}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
